import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ShowToastMessage } from "../helper/ShowToastMessage";
import ParentHeader from "../Parent/ParentHeader/ParentHeader";
import SiteHeader from "../Site/SiteHeader";
import SiteFooter from "../Site/SiteFooter";
import { signedIn as hasSession } from "../Site/session";
import { usePageMeta } from "../Site/useSiteMotion";
import { LOGOUT, SIGNIN } from "../redux/actionTypes/authActionType";
import { FINISH_NAME, PhotoPicker } from "./AdjudicatorProfileForm";
import {
  acceptWithNewAccount,
  savePhoto,
  claimWithAccount,
  withChosenName,
  errorText,
  getInvitation,
  refusal,
  signIn,
  storeSession,
} from "./adjudicatorApi";
import "../Site/site.css";
import "./adjudicator.css";

// /adjudicator/invite/:token (MC2 card 930d3ac0): the link a booked
// adjudicator is emailed. One page (Devin 2026-10-26): the booking that sent
// it, their own photo (required; the one Feis.link had on file isn't shown)
// beside their CLRG directory name and country (not editable), and the
// account: a new one (no email code - the link proves the address), or
// signing in to the parent/teacher account that email already has, which then
// gains the adjudicator role. The photo is uploaded once the account is in
// place. Open signed in or out.

const NEED_PHOTO = "Add your photo above first.";


export const Bookings = ({ bookings }) =>
  bookings?.length ? (
    <ul className="adj-bookings">
      {bookings.map((b) => (
        <li key={`${b.kind}-${b.eventId}`}>
          <span className="adj-bk-name">{b.name}</span>
          <span className="adj-bk-meta">{[b.day, b.place].filter(Boolean).join(" · ")}</span>
        </li>
      ))}
    </ul>
  ) : null;

// "Add your photo above first" goes away once they have.
const useClearNeedPhoto = (hasPhoto, setError) =>
  useEffect(() => {
    if (hasPhoto) setError((e) => (e === NEED_PHOTO ? "" : e));
  }, [hasPhoto, setError]);

// "Finish your name first" goes away once the name boxes are closed.
const useClearFinishName = (nameEditing, setError) =>
  useEffect(() => {
    if (!nameEditing) setError((e) => (e === FINISH_NAME ? "" : e));
  }, [nameEditing, setError]);

const NewAccountForm = ({ token, invite, hasPhoto, chosenName, nameEditing, onBusy, onDone }) => {
  // the region comes from the record (the CLRG directory, or set by admin for
  // a judge not listed): never asked here (Devin 2026-10-27)
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useClearNeedPhoto(hasPhoto, setError);
  useClearFinishName(nameEditing, setError);
  // the name pencil is locked while the account request runs
  useEffect(() => { if (onBusy) onBusy(busy); }, [busy, onBusy]);
  const dispatch = useDispatch();

  const submit = async (e) => {
    e.preventDefault();
    if (!hasPhoto) return setError(NEED_PHOTO);
    if (nameEditing) return setError(FINISH_NAME);
    if (password.length < 8) return setError("Choose a password of at least 8 characters.");
    if (password !== repeat) return setError("The two passwords don't match.");
    setBusy(true);
    setError("");
    try {
      const res = await acceptWithNewAccount(token, { password, ...withChosenName(chosenName) });
      if (!res?.success || !res?.accessToken) throw refusal(res?.message || "ServerError");
      // accepting grants the role by definition
      storeSession({ ...res, adjudicator: true });
      dispatch({ type: SIGNIN, payload: res });
      onDone();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Your account wasn't created. Please try again."));
    }
  };

  return (
    <form className="adj-form" onSubmit={submit} noValidate>
      <div className="adj-field">
        <span className="adj-label">Email</span>
        <p className="adj-readonly">{invite.email}</p>
      </div>
      <div className="adj-row">
        <div className="adj-field">
          <label className="adj-label" htmlFor="na-pw">Choose a password</label>
          <input id="na-pw" className="adj-in" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="adj-field">
          <label className="adj-label" htmlFor="na-pw2">Repeat password</label>
          <input id="na-pw2" className="adj-in" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </div>
      </div>
      {error && <p className="adj-error" role="alert">{error}</p>}
      <button type="submit" className="adj-btn primary adj-submit" disabled={busy}>
        {busy ? "Setting up…" : "Create my account"}
      </button>
    </form>
  );
};

const SignInToAccept = ({ token, invite, hasPhoto, chosenName, nameEditing, onBusy, onDone, signedIn, onSignedIn, onSignedOut }) => {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useClearNeedPhoto(hasPhoto, setError);
  useClearFinishName(nameEditing, setError);
  useEffect(() => { if (onBusy) onBusy(busy); }, [busy, onBusy]);
  const dispatch = useDispatch();

  const claim = async () => {
    await claimWithAccount(token, withChosenName(chosenName));
    localStorage.setItem("isAdjudicator", true);
    onDone();
  };

  const acceptSignedIn = async () => {
    if (!hasPhoto) return setError(NEED_PHOTO);
    if (nameEditing) return setError(FINISH_NAME);
    setBusy(true);
    setError("");
    try {
      await claim();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "The invitation wasn't accepted. Please try again."));
    }
  };

  const signInAndAccept = async (e) => {
    e.preventDefault();
    if (!password) return;
    if (!hasPhoto) return setError(NEED_PHOTO);
    if (nameEditing) return setError(FINISH_NAME);
    setBusy(true);
    setError("");
    try {
      const res = await signIn(invite.email, password);
      if (!res?.success || !res?.accessToken) throw refusal(res?.message || "Wrong email or password.");
      storeSession(res);
      dispatch({ type: SIGNIN, payload: res });
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "Wrong email or password."));
      return;
    }
    // signed in now: the page shows the signed-in Accept (with Sign out) if
    // the claim fails, with the claim's own message
    onSignedIn();
    try {
      await claim();
    } catch (err) {
      setBusy(false);
      setError(errorText(err, "The invitation wasn't accepted. Please try again."));
    }
  };

  const signOut = () => {
    dispatch({ type: LOGOUT });
    localStorage.clear();
    onSignedOut();
  };

  if (signedIn) {
    return (
      <div className="adj-form">
        <p className="adj-body">
          This invitation is for <b>{invite.email}</b>. Accept it with that Feis.link account: your adjudicator profile is added to it, so you keep one login for everything.
        </p>
        {error && <p className="adj-error" role="alert">{error}</p>}
        <div className="adj-acts">
          <button type="button" className="adj-btn primary" onClick={acceptSignedIn} disabled={busy}>
            {busy ? "Accepting…" : "Accept invitation"}
          </button>
          <button type="button" className="adj-btn ghost" onClick={signOut}>
            {error ? "Sign out" : `Not ${invite.email}? Sign out`}
          </button>
        </div>
      </div>
    );
  }
  return (
    <form className="adj-form" onSubmit={signInAndAccept} noValidate>
      <p className="adj-body">
        <b>{invite.email}</b> already has a Feis.link account. Sign in to accept: your adjudicator profile is added to that account, so you keep one login for everything.
      </p>
      <div className="adj-field">
        <label className="adj-label" htmlFor="si-pw">Password</label>
        <input id="si-pw" className="adj-in" type="password" autoComplete="current-password" value={password}
          onChange={(e) => { setPassword(e.target.value); setError(""); }} />
      </div>
      {error && <p className="adj-error" role="alert">{error}</p>}
      <div className="adj-acts">
        <button type="submit" className="adj-btn primary" disabled={!password || busy}>
          {busy ? "Signing in…" : "Sign in and accept"}
        </button>
        <Link className="adj-link" to="/forgot-password">Forgot password?</Link>
      </div>
    </form>
  );
};

const AdjudicatorInvite = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [step, setStep] = useState("account");
  const [signedIn, setSignedIn] = useState(hasSession());
  const [photo, setPhoto] = useState(null);
  // the name the judge chose with the pencil (null: the listed one); whether
  // its boxes are open; and an account request in flight (locks the pencil)
  const [chosenName, setChosenName] = useState(null);
  const [nameEditing, setNameEditing] = useState(false);
  const [accountBusy, setAccountBusy] = useState(false);
  // the photo on screen when the account request finishes (the judge may
  // change it while that request is in flight)
  const photoRef = useRef(null);
  const [photoError, setPhotoError] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  usePageMeta("Set up your adjudicator profile", "Accept your Feis.link adjudicator invitation.");

  useEffect(() => {
    let live = true;
    getInvitation(token)
      .then((res) => live && setState({ invite: res }))
      .catch((err) => live && setState({ gone: errorText(err, "This invitation link is no longer valid.") }));
    return () => { live = false; };
  }, [token]);

  const { loading, gone, invite } = state;
  const title = "Adjudicator invitation";

  // the account is in place: upload the photo (retryable on its own)
  const finish = async () => {
    setSignedIn(true);
    setStep("photo");
    setSaving(true);
    setPhotoError("");
    try {
      // upload whatever is on screen; if they reframe while it uploads, send that too
      let sent;
      do {
        sent = photoRef.current;
        await savePhoto(sent.blob);
      } while (photoRef.current !== sent);
      // all set: on to their dashboard, like any sign-in (Devin 2026-10-26)
      ShowToastMessage("success", "You're all set. Your adjudicator profile is ready.", 4000);
      navigate("/parent", { replace: true });
    } catch (err) {
      setPhotoError(errorText(err, "Your account is set up, but your photo didn't upload. Please try again."));
    } finally {
      setSaving(false);
    }
  };
  // keyed so the account step and the photo-retry step reuse one picker (its
  // framed photo, Adjust source and object URLs survive the switch)
  const photoBlock = invite && (
    <section className="adj-sec" key="photo">
      <h2 className="adj-h2">How you'll appear</h2>
      <PhotoPicker
        preview={photo?.preview}
        profile={invite.profile}
        onPick={(p) => { photoRef.current = p; setPhoto(p); setPhotoError(""); }}
        onError={setPhotoError}
        disabled={saving || (step === "account" && accountBusy)}
        prompt={photo ? "" : "Add a clear head-and-shoulders photo of yourself. It's shown with your name on event pages and results."}
        chosenName={chosenName}
        onChooseName={step === "account" ? setChosenName : undefined}
        onNameEditing={setNameEditing}
      />
      {photoError && <p className="adj-error" role="alert">{photoError}</p>}
    </section>
  );

  let content = null;
  if (loading) content = <p className="adj-body">Loading…</p>;
  else if (gone) {
    content = (
      <>
        <p className="adj-body">{gone}</p>
        <p className="adj-body">If you've already set up your profile, sign in to see it.</p>
        {!signedIn && <div className="adj-acts"><Link className="sbtn" to="/login">Sign in</Link></div>}
      </>
    );
  } else if (step === "account") {
    content = (
      <>
        <p className="adj-kicker">You're booked to adjudicate</p>
        <Bookings bookings={invite.bookings} />
        {(invite.account === "none" || invite.account === "user") && photoBlock}
        {invite.account === "none" && (
          <section className="adj-sec">
            <h2 className="adj-h2">Create your account</h2>
            {signedIn && (
              <div className="adj-acts">
                <p className="adj-body">Another account is signed in on this browser. Creating your adjudicator account signs it out.</p>
                <button type="button" className="adj-btn ghost" onClick={() => {
                  dispatch({ type: LOGOUT });
                  localStorage.clear();
                  setSignedIn(false);
                }}>Sign out now</button>
              </div>
            )}
            <NewAccountForm token={token} invite={invite} hasPhoto={!!photo} chosenName={chosenName} nameEditing={nameEditing} onBusy={setAccountBusy} onDone={finish} />
          </section>
        )}
        {invite.account === "user" && (
          <section className="adj-sec">
            <h2 className="adj-h2">Accept with your account</h2>
            <SignInToAccept token={token} invite={invite} hasPhoto={!!photo} chosenName={chosenName} nameEditing={nameEditing} onBusy={setAccountBusy} signedIn={signedIn}
              onSignedIn={() => setSignedIn(true)}
              onSignedOut={() => setSignedIn(false)}
              onDone={finish} />
          </section>
        )}
        {(invite.account === "organizer" || invite.account === "admin") && (
          <p className="adj-error">
            {invite.email} is an organiser login, which can't hold an adjudicator profile. Please contact Feis.link to set up your adjudicator profile.
          </p>
        )}
      </>
    );
  } else if (step === "photo") {
    content = (
      <>
        <p className="adj-body">Your account is set up.</p>
        {photoBlock}
        <button type="button" className="adj-btn primary adj-submit" onClick={finish} disabled={saving || !photo}>
          {saving ? "Uploading your photo…" : "Upload my photo"}
        </button>
      </>
    );
  }

  return (
    <div className={`site adj${signedIn ? "" : " adj-out"}`}>
      {signedIn ? <ParentHeader /> : <SiteHeader />}
      <main>
        <section className="hero adj-hero">
          <div className="wrap">
            <span className="eyebrow">Adjudicators</span>
            <h1 className="adj-title">{title}</h1>
          </div>
        </section>
        <div className="wrap">
          <div className="adj-card">{content}</div>
        </div>
      </main>
      {!signedIn && <SiteFooter />}
    </div>
  );
};

export default AdjudicatorInvite;
